import type { ReactNode } from 'react';
import type { ExpectedField } from '@discovr/types';
import { Info } from '../../../icons';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/ui/popover';

interface SheetRulesPopoverProps {
  /** Campaign payout model — drives the ₹0 line (allowed only on gifting / revenue-share). */
  payoutModel?: string;
  /** The server's own column list. Read here only for `required` on the metric
   *  columns — Instagram now fetches engagement rate and views itself, YouTube
   *  still needs them in the sheet. Deriving it from the server's answer keeps
   *  this text honest without duplicating the platform rule on the client. */
  expectedFields?: ExpectedField[];
}

const Code = ({ children }: { children: ReactNode }) => (
  <code className="rounded-sm bg-muted px-1 font-mono text-caption-regular text-foreground">{children}</code>
);

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="mb-1 text-[9px] font-bold tracking-widest text-muted-foreground uppercase">
        {title}
      </div>
      <ul className="list-disc space-y-1 pl-4 marker:text-muted-foreground">{children}</ul>
    </div>
  );
}

/**
 * A grouped, payout-aware reference of the sheet import / re-import rules, shown
 * next to the upload control so the admin team knows the conventions without
 * tribal knowledge. The ₹0 line flips on the campaign's payout model.
 */
export function SheetRulesPopover({ payoutModel, expectedFields }: SheetRulesPopoverProps) {
  const zeroAllowed = payoutModel === 'gifting_only' || payoutModel === 'revenue_share';
  const metricsRequired = expectedFields?.some((f) => f.key === 'engagement_rate' && f.required) ?? true;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          <Info size={12} /> Sheet rules
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        // max-h caps the height when rendered inside a modal so the popover
        // doesn't grow past the visible area. onWheel stopPropagation prevents
        // react-remove-scroll (used by Radix Dialog) from intercepting wheel
        // events at the document level — without it, two-finger trackpad scroll
        // is blocked because the popover Portal sits outside the Dialog subtree.
        onWheel={(e) => e.stopPropagation()}
        className="max-h-[min(520px,var(--radix-popover-content-available-height))] w-[540px] overflow-y-auto p-4 text-caption-medium/relaxed text-muted-foreground"
      >
        <div className="mb-3 text-body-sm-semibold text-foreground">Sheet import rules</div>

        <Section title="Required columns">
          <li>
            <Code>username</Code> (or <Code>un</Code>) — the creator's handle (see Usernames
            below).
          </li>
          {metricsRequired && (
            <li>
              <Code>engagement_rate</Code> (or <Code>eng_rate</Code> / <Code>er</Code>) and{' '}
              <Code>avg_views</Code> (or <Code>av</Code>) — must be numbers greater than zero.
            </li>
          )}
        </Section>

        <Section title="Optional columns">
          {!metricsRequired && (
            <li>
              <Code>engagement_rate</Code> and <Code>avg_views</Code> — leave them out and we read
              them off the creator's recent reels after the import. Include them and your numbers
              are used as-is.
            </li>
          )}
          <li>
            <Code>first_name</Code> (or <Code>fn</Code>) and <Code>last_name</Code> (or{' '}
            <Code>ln</Code>) — used to pre-fill the creator's display name.
          </li>
          <li>
            <Code>email</Code> — ignored if present (no longer needed).
          </li>
          <li>
            Deliverable columns accept a short platform prefix — <Code>ig_</Code> instead of{' '}
            <Code>instagram_</Code>, <Code>yt_</Code> instead of <Code>youtube_</Code> (e.g.{' '}
            <Code>instagram_non_collab_reel</Code> or <Code>ig_non_collab_reel</Code>).
          </li>
          <li>Column headers are flexible — capitalisation, spaces, and underscores all work.</li>
        </Section>

        <Section title="When a row is skipped">
          <li>The username is missing or blank.</li>
          {metricsRequired && (
            <li>Engagement rate or average views is missing or not a positive number.</li>
          )}
          <li>A name column is longer than 100 characters.</li>
          <li>
            The same username appears more than once <em>with identical values</em> — the first row
            is kept, the rest dropped. If the duplicate rows disagree, review pauses and asks you to
            pick the one to keep.
          </li>
          <li>
            No <strong>main</strong> deliverable ends up priced. Add-ons alone are not enough — a row
            that prices only an add-on is still skipped.
          </li>
        </Section>

        <Section title="Deliverable prices">
          <li>
            <strong>Any positive number</strong> — sets that deliverable at the given price.
          </li>
          <li className={zeroAllowed ? 'font-medium text-emerald-700' : 'font-medium text-rose-600'}>
            <strong>₹0</strong> —{' '}
            {zeroAllowed
              ? 'allowed on this campaign type (gifting / revenue-share). Imports the deliverable as free/barter.'
              : 'not a valid price on this campaign type. Treated as blank — that deliverable is skipped.'}
          </li>
          <li>
            Currency symbols and commas are fine — <Code>₹5,000</Code> imports as 5000.
          </li>
          <li>
            <strong>Units are read automatically</strong> — <Code>12k</Code> imports as 12000,{' '}
            <Code>1.5 lakh</Code> as 150000, <Code>2 crore</Code> as 20000000, and{' '}
            <Code>5000/-</Code> as 5000. Only text that still isn't a readable amount after that is
            flagged at the review step for you to retype.
          </li>
          <li>
            <strong>Extreme values are rejected</strong> — anything over ₹1,000,000,000 (in either
            direction) is treated as unreadable rather than imported as-is.
          </li>
          <li>
            <strong>Blank or wordy</strong> (e.g. "free", "50%") — that deliverable is skipped. The
            rest of the row is still imported.
          </li>
          <li>
            <strong>The word "remove"</strong> — removes that deliverable from the creator entirely.
          </li>
          <li>
            <strong>A negative number</strong> (e.g. <Code>-1</Code>) is flagged at the review step,
            not read as a removal — retype it as a positive price or the word "remove".
          </li>
        </Section>

        <Section title="Re-importing">
          <li>
            Upload the sheet again at any time — new creators are added, existing ones are updated.
            No creator is ever removed by a re-import.
          </li>
          <li>
            Prices are only updated while the creator is still <Code>pending</Code>. Once a brand
            accepts them, their agreed prices are locked — re-importing refreshes their stats but
            won't change prices.
          </li>
          <li>
            A stat column you leave out is left alone, not blanked — existing figures stay until the
            next platform fetch replaces them.
          </li>
        </Section>

        <Section title="Usernames">
          <li>
            <Code>@handle</Code>, full profile URLs, different capitalisation — they all resolve to
            the same creator.
          </li>
          <li>
            A profile link from the wrong platform (a YouTube link in an Instagram campaign) is
            flagged at the review step rather than imported.
          </li>
        </Section>
      </PopoverContent>
    </Popover>
  );
}
